import { useState } from 'react';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
} from '@redbax/ui-kit';
import { useRequestAgentDownload, type AgentPlatform } from '@redbax/shared';
import { Copy, Check, Plus, Terminal, Download, Loader2 } from 'lucide-react';

/**
 * Adding a device, described the way it actually works.
 *
 * THIS DIALOG USED TO DESCRIBE A PATH THAT DID NOT EXIST. It showed one install command —
 * `msiexec /i Redbax.Agent.Installer.msi JOIN_TOKEN=… /qn` — whichever platform button you clicked,
 * so a Linux user was handed a Windows command. The MSI filename was wrong (the download serves
 * `RedbaxAgent-<version>-x64.msi`). And `JOIN_TOKEN=` enrolled nothing at all: the agent has no `join`
 * verb, and the installer's custom actions used `Return="ignore"`, so the install reported success.
 *
 * Enrolment is BROWSER SIGN-IN on both platforms. The Linux installer runs `redbax-agent login`
 * itself; the Windows tray prompts "Sign in required" after install. Both open `/agent/authorize`,
 * where the person approves the device against their own organization — which is also why there is no
 * token to copy here any more. Nothing in the agent consumes a join token: `/api/devices/join` is a
 * complete server API with no client, and a freshly provisioned organization has no token anyway, so
 * this dialog used to dead-end on "No join token available. Contact your administrator." shown to the
 * administrator.
 */

const PLATFORMS: Array<{ id: AgentPlatform; label: string }> = [
  { id: 'windows', label: 'Windows' },
  { id: 'linux', label: 'Linux' },
];

// Linux is one public line; Windows is a download and a double-click. Kept identical to the
// guided first run (WelcomePage), so the two places that explain installing cannot drift.
const INSTALL_COMMAND: Record<AgentPlatform, string | null> = {
  windows: null,
  linux: 'curl -fsSL https://get.redbax.io | sudo sh',
};

const SIGN_IN_STEP: Record<AgentPlatform, string> = {
  windows: 'The Redbax tray icon prompts "Sign in required" — click it and approve the device in the browser that opens.',
  linux: 'The installer opens a browser for you. Approve the device there to finish.',
};

const STEPS: Record<AgentPlatform, string[]> = {
  windows: ['Download the installer', 'Run it on that machine'],
  linux: ['Run the command below on that machine'],
};

export function AddDeviceButton() {
  const requestDownload = useRequestAgentDownload();
  const [platform, setPlatform] = useState<AgentPlatform>('windows');
  const [downloading, setDownloading] = useState<AgentPlatform | null>(null);
  const [copied, setCopied] = useState(false);

  const handleDownload = async (target: AgentPlatform) => {
    setDownloading(target);
    try {
      const { url } = await requestDownload.mutateAsync(target);
      // Content-Disposition: attachment on the Worker response triggers a download
      // without navigating away from the dashboard.
      const link = document.createElement('a');
      link.href = url;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      // surfaced via requestDownload.isError below
    } finally {
      setDownloading(null);
    }
  };

  const command = INSTALL_COMMAND[platform];

  const handleCopy = async () => {
    if (!command) return;
    await navigator.clipboard.writeText(command);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog>
      <DialogTrigger className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors bg-red-accent text-text-primary hover:bg-red-hover h-10 px-4 py-2 disabled:pointer-events-none disabled:opacity-50">
        <Plus className="mr-2 h-4 w-4" />
        Add Device
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a Device</DialogTitle>
          <DialogDescription>
            Install the Redbax endpoint agent on a device to start protecting AI tool interactions.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="flex gap-2">
            {PLATFORMS.map((p) => (
              <Button
                key={p.id}
                variant={platform === p.id ? 'default' : 'secondary'}
                size="sm"
                onClick={() => setPlatform(p.id)}
              >
                {p.label}
              </Button>
            ))}
          </div>

          <ol className="list-decimal list-inside space-y-2 text-sm text-text-secondary">
            {STEPS[platform].map((step) => (
              <li key={step}>{step}</li>
            ))}
            <li>{SIGN_IN_STEP[platform]}</li>
          </ol>

          {command ? (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-text-secondary">Install command</label>
              <div className="flex items-start gap-2 rounded-lg bg-surface-raised p-3 font-mono text-xs">
                <Terminal className="mt-0.5 h-4 w-4 shrink-0 text-text-secondary" />
                <code className="flex-1 whitespace-pre-wrap break-all">{command}</code>
                <Button variant="ghost" size="sm" onClick={handleCopy} className="shrink-0">
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Button
                variant="secondary"
                size="sm"
                disabled={downloading !== null}
                onClick={() => handleDownload(platform)}
              >
                {downloading === platform ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                Download for {PLATFORMS.find((p) => p.id === platform)!.label}
              </Button>
              {requestDownload.isError && (
                <p className="text-xs text-status-error">
                  Couldn&rsquo;t start the download. Please try again.
                </p>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
